import { api } from '../api/endpoints'
import { isNetworkError } from '../api/client'
import type { ChannelEvent, SyncResponse } from '../api/types'
import { db } from '../db/db'
import { metaKeys, setMeta } from '../db/meta'
import { currentMeId } from '../state/session'
import { isOnline } from '../state/connection'
import { applyEvents, applySyncResult, upsertUsers, type ApplyOutcome } from './applyEvents'
import { ensureUsers, eventUserIds } from './ensureUsers'

export type SyncTransport = (body: Parameters<typeof api.sync>[0]) => Promise<SyncResponse>

let transport: SyncTransport = (body) => api.sync(body)

/** Test seam. */
export function setSyncTransport(next: SyncTransport): void {
  transport = next
}

let inflight: Promise<void> | null = null
let again = false
const listeners = new Set<(ok: boolean) => void>()

export function onSyncFinished(listener: (ok: boolean) => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Apply a whole sync response. Exported for tests. */
export async function applySyncResponse(response: SyncResponse, meId: string): Promise<boolean> {
  await upsertUsers(response.users)
  let needsResync = false
  for (const result of response.channels) {
    const outcome = await applySyncResult(result, meId)
    if (outcome.needsResync) needsResync = true
  }
  await cleanupConfirmedOutbox()
  await setMeta(metaKeys.lastSyncAt, response.serverTime)
  return needsResync
}

/** Outbox rows whose message already exists locally were delivered. */
export async function cleanupConfirmedOutbox(): Promise<void> {
  await db.transaction('rw', [db.outbox, db.messages], async () => {
    const rows = await db.outbox.toArray()
    if (!rows.length) return
    const found = await db.messages.bulkGet(rows.map((r) => r.id))
    const delivered = rows.filter((_, i) => found[i]).map((r) => r.id)
    if (delivered.length) await db.outbox.bulkDelete(delivered)
  })
}

async function syncOnce(): Promise<boolean> {
  const meId = currentMeId()
  if (!meId || !isOnline()) return false
  const channels = await db.channels.toArray()
  const response = await transport({ channels: channels.map((c) => ({ id: c.id, version: c.version, summaryHash: c.summaryHash ?? null })) })
  const needsResync = await applySyncResponse(response, meId)
  if (needsResync) again = true
  return true
}

async function run(): Promise<void> {
  let ok = false
  let rounds = 0
  try {
    do {
      again = false
      ok = await syncOnce()
      rounds++
    } while (again && rounds < 5)
  } catch (error) {
    ok = false
    if (!isNetworkError(error)) console.warn('sync failed', error)
  } finally {
    for (const listener of listeners) listener(ok)
  }
}

/**
 * POST /api/sync with every local channel. Concurrent callers share one run; a call made while a
 * run is in flight schedules exactly one more round so nothing requested is lost.
 */
export function syncAll(): Promise<void> {
  if (inflight) {
    again = true
    return inflight
  }
  inflight = run().finally(() => {
    inflight = null
  })
  return inflight
}

/**
 * Sync because one channel has a gap. The server returns resync results for member channels that
 * are missing from the request, so the request always lists every local channel.
 */
export function syncChannel(_channelId: string): Promise<void> {
  return syncAll()
}

/** Mark a channel for a snapshot download (ForceResync) and sync. */
export async function forceResync(channelId: string): Promise<void> {
  await db.channels.update(channelId, { version: null })
  await syncAll()
}

/** Entry point for live ChannelEvents (hub) and REST mutation responses. */
export async function receiveEvents(events: ChannelEvent[]): Promise<ApplyOutcome[]> {
  const meId = currentMeId()
  if (!meId || events.length === 0) return []
  const outcomes = await applyEvents(events, meId)
  // Someone whose first message arrives live may be new to this device (sync names only the people in its response).
  void ensureUsers(events.flatMap(eventUserIds))
  if (outcomes.some((o) => o === 'gap' || o === 'unknown_channel')) {
    void syncChannel(events[0].channelId)
  }
  return outcomes
}

export function receiveEvent(event: ChannelEvent | undefined | null): Promise<ApplyOutcome[]> {
  if (!event || typeof event !== 'object' || !('seq' in event)) return Promise.resolve([])
  return receiveEvents([event])
}
